import type { LucideIcon } from 'lucide-react'
import {
  Sun,
  Brain,
  Activity,
  Zap,
  BarChart2,
  CheckSquare,
  RefreshCw,
  Droplets,
  GitPullRequest,
  Quote,
  CalendarDays,
  Mail,
  FileText,
  BookOpen,
} from 'lucide-react'
import { meetsMinimum, type UserRole } from '@/shared/lib/auth'

export type TileStatus = 'stable' | 'beta' | 'dev-only' | 'coming-soon'

export interface TileMetadata {
  name: string
  description: string
  icon: LucideIcon
  status: TileStatus
  defaultProps: Record<string, unknown>
  propsSchema: Record<string, { type: string; label: string; required?: boolean; placeholder?: string }>
}

/**
 * Tile access matrix (RFC-002). Single source of truth for "which roles may
 * see a tile of this status" — consumed by both `Home.tsx` (render-time
 * gating of the persisted layout) and `SettingsDashboard.tsx` (which tiles
 * can be toggled on) so the two surfaces cannot drift apart.
 *
 *   stable       → everyone
 *   beta         → dev+
 *   dev-only     → dev+
 *   coming-soon  → everyone (rendered as a locked preview for members; the
 *                  Settings *toggle* — not visibility — is separately gated)
 */
export function canRoleSeeTile(status: TileStatus, role: UserRole): boolean {
  if (status === 'beta' || status === 'dev-only') {
    return meetsMinimum(role, 'dev')
  }
  return true
}

export const TILE_METADATA: Record<string, TileMetadata> = {
  DailyBriefing: {
    name: 'Daily Briefing',
    description: 'AI-generated summary of your day — tasks, risks, and signals',
    icon: Sun,
    status: 'coming-soon',
    defaultProps: {},
    propsSchema: {},
  },
  DeepWork: {
    name: 'Deep Work',
    description: 'Focus sessions with timer and session tracking',
    icon: Brain,
    status: 'stable',
    defaultProps: {},
    propsSchema: {},
  },
  Biometrics: {
    name: 'Biometrics',
    description: 'Health metrics and body data',
    icon: Activity,
    status: 'coming-soon',
    defaultProps: {},
    propsSchema: {},
  },
  QuickActions: {
    name: 'Quick Actions',
    description: 'Shortcuts to common tasks and automations',
    icon: Zap,
    status: 'dev-only',
    defaultProps: {},
    propsSchema: {},
  },
  QuickStats: {
    name: 'Quick Stats',
    description: 'At-a-glance counts for tasks and habits today',
    icon: BarChart2,
    status: 'coming-soon',
    defaultProps: { title: 'Stats' },
    propsSchema: {
      title: { type: 'text', label: 'Title', placeholder: 'Stats' },
    },
  },
  TaskList: {
    name: 'Task List',
    description: 'View and manage your tasks',
    icon: CheckSquare,
    status: 'stable',
    defaultProps: { title: 'My Tasks' },
    propsSchema: {
      title: { type: 'text', label: 'Title', placeholder: 'My Tasks' },
    },
  },
  HabitTracker: {
    name: 'Habit Tracker',
    description: 'Log and track your daily habits',
    icon: RefreshCw,
    status: 'stable',
    defaultProps: { title: 'Daily Habits' },
    propsSchema: {
      title: { type: 'text', label: 'Title', placeholder: 'Daily Habits' },
    },
  },
  WaterIntake: {
    name: 'Water Intake',
    description: 'Track daily water consumption',
    icon: Droplets,
    status: 'stable',
    defaultProps: { title: 'Water Intake', dailyGoal: 8 },
    propsSchema: {
      title: { type: 'text', label: 'Title', placeholder: 'Water Intake' },
      dailyGoal: { type: 'number', label: 'Daily Goal (glasses)', placeholder: '8' },
    },
  },
  WorkflowQueue: {
    name: 'Workflow Queue',
    description: 'Pending AI improvement workflows awaiting approval',
    icon: GitPullRequest,
    status: 'dev-only',
    defaultProps: {},
    propsSchema: {},
  },
  DailyStoic: {
    name: 'Daily Stoic',
    description: 'A daily philosophical quote to start your day',
    icon: Quote,
    status: 'coming-soon',
    defaultProps: {},
    propsSchema: {},
  },
  Agenda: {
    name: 'Agenda',
    description: 'Upcoming calendar events and scheduled tasks',
    icon: CalendarDays,
    status: 'stable',
    defaultProps: {},
    propsSchema: {},
  },
  Email: {
    name: 'Email',
    description: 'Gmail inbox preview with quick actions',
    icon: Mail,
    status: 'coming-soon',
    defaultProps: {},
    propsSchema: {},
  },
  Notes: {
    name: 'Notes',
    description: 'Simple notepad for quick thoughts',
    icon: FileText,
    status: 'stable',
    defaultProps: { title: 'New Note' },
    propsSchema: {
      title: { type: 'text', label: 'Title', required: true, placeholder: 'My Notes' },
    },
  },
  BookTracker: {
    name: 'Book Tracker',
    description: 'Track your reading progress and queue',
    icon: BookOpen,
    status: 'coming-soon',
    defaultProps: {},
    propsSchema: {},
  },
}
